import React from 'react';
import { Box, Container, Paper, Typography, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const Section = ({ n, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
      {n}. {title}
    </Typography>
    <Box sx={{ color: 'text.secondary', '& p': { mb: 1.5 } }}>{children}</Box>
  </Box>
);

const Terms = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Terms &amp; Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last updated: 11 June 2026
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Section n="1" title="Introduction & Acceptance">
          <Typography component="p" variant="body2">
            These Terms &amp; Conditions ("Terms") govern the access to and use of the ClassTree school management
            platform ("ClassTree", "the Platform", "Service"), operated by AJ Tech Labs ("we", "us", "our"). By
            creating an account, logging in, or otherwise using the Platform, the School and its authorised users
            agree to be bound by these Terms. If you do not agree, please do not use the Service.
          </Typography>
        </Section>

        <Section n="2" title="Definitions">
          <Typography component="p" variant="body2">
            "School" means the educational institution that subscribes to ClassTree.
          </Typography>
          <Typography component="p" variant="body2">
            "User" means any person authorised to access the Platform, including Principals, School Administrators,
            Teachers, and Parents.
          </Typography>
          <Typography component="p" variant="body2">
            "Content" means all data entered into or generated through the Platform, including student records,
            attendance, marks, fees, report cards, and communications.
          </Typography>
          <Typography component="p" variant="body2">
            "Student Data" means personal information relating to students enrolled at a School.
          </Typography>
        </Section>

        <Section n="3" title="Eligibility & Accounts">
          <Typography component="p" variant="body2">
            The Platform is intended for use by registered educational institutions and their authorised personnel
            and the parents/guardians of enrolled students. Accounts are created by the School or by us on the
            School's instruction. Users are responsible for keeping their login credentials confidential and for all
            activity under their accounts. Notify us immediately of any unauthorised access at{' '}
            <Link href="mailto:support@classtree.in" underline="hover">support@classtree.in</Link>.
          </Typography>
        </Section>

        <Section n="4" title="Roles & Responsibilities">
          <Typography component="p" variant="body2">
            Access to features is determined by role (Principal/School Administrator, Teacher, Parent). Each User
            agrees to use only the access granted to them, to enter accurate information, and not to attempt to
            access data belonging to other schools or users. The School is responsible for managing the User
            accounts it creates and for the accuracy of data it provides.
          </Typography>
        </Section>

        <Section n="5" title="Subscription & Fees">
          <Typography component="p" variant="body2">
            Access to ClassTree is provided on a subscription basis as agreed between the School and AJ Tech Labs.
            Subscription tiers, billing periods, and applicable taxes are set out in the order or invoice agreed
            with the School. Fees are payable in advance unless otherwise agreed. Non-payment may result in
            suspension of access following reasonable notice.
          </Typography>
        </Section>

        <Section n="6" title="Acceptable Use">
          <Typography component="p" variant="body2">
            Users agree not to: (a) misuse, copy, or attempt to reverse-engineer the Platform; (b) upload
            unlawful, harmful, or infringing content; (c) interfere with the Platform's security or availability;
            (d) use the Service to send spam or unsolicited communications; or (e) use the Platform in violation
            of any applicable law.
          </Typography>
        </Section>

        <Section n="7" title="Data Ownership & Privacy">
          <Typography component="p" variant="body2">
            The School owns the Content it submits, including Student Data. With respect to Student Data, the
            School acts as the entity responsible for that data, and AJ Tech Labs processes it solely on the
            School's behalf to provide the Service. We will not sell Student Data or use it for advertising.
            Processing of personal data is carried out in accordance with applicable Indian data-protection law,
            including the Digital Personal Data Protection Act, 2023.
          </Typography>
          <Typography component="p" variant="body2">
            Where the Platform processes data relating to children, the School is responsible for obtaining and
            maintaining any required parental or guardian consent.
          </Typography>
        </Section>

        <Section n="8" title="Data Security & Tenant Isolation">
          <Typography component="p" variant="body2">
            We implement reasonable technical and organisational measures to protect Content, including encryption
            of data in transit and strict logical separation of each School's data so that one School cannot access
            another School's records. While we take security seriously, no system is completely secure, and we
            cannot guarantee absolute security.
          </Typography>
        </Section>

        <Section n="9" title="Communications & Notifications">
          <Typography component="p" variant="body2">
            The Platform may send notifications by email, in-app messages, and (where enabled) WhatsApp, including
            alerts about attendance, marks, fees, meetings, and care notes. By providing contact details and
            enabling these channels, Users consent to receiving such service-related communications. Standard
            messaging rates from third-party providers may apply.
          </Typography>
        </Section>

        <Section n="10" title="Third-Party Services">
          <Typography component="p" variant="body2">
            The Platform integrates third-party services to deliver certain features, which may include AI
            assistance and messaging providers (for example, OpenAI for the chatbot and Twilio for WhatsApp). Use
            of these features may be subject to the respective providers' terms. We are not responsible for the
            availability, accuracy, or performance of third-party services.
          </Typography>
        </Section>

        <Section n="11" title="Intellectual Property">
          <Typography component="p" variant="body2">
            The Platform, including its software, design, branding, and documentation, is the property of AJ Tech
            Labs and is protected by applicable intellectual-property laws. These Terms do not transfer any
            ownership in the Platform to the School or Users. The "ClassTree" name and logo may not be used
            without our written permission.
          </Typography>
        </Section>

        <Section n="12" title="Service Availability & Maintenance">
          <Typography component="p" variant="body2">
            We aim to keep the Platform available and reliable but do not guarantee uninterrupted access. The
            Service may be temporarily unavailable for scheduled maintenance, updates, or reasons beyond our
            control. We will make reasonable efforts to give advance notice of planned downtime.
          </Typography>
        </Section>

        <Section n="13" title="Suspension & Termination">
          <Typography component="p" variant="body2">
            We may suspend or terminate access for breach of these Terms, non-payment, or activity that threatens
            the security or integrity of the Platform. A School may discontinue its subscription in accordance
            with its agreement with us. On termination, we will, on the School's written request within a
            reasonable period, make the School's data available for export, after which it may be deleted in
            accordance with our retention practices.
          </Typography>
        </Section>

        <Section n="14" title="Limitation of Liability">
          <Typography component="p" variant="body2">
            To the maximum extent permitted by law, AJ Tech Labs shall not be liable for any indirect, incidental,
            or consequential losses, or for loss of data, profits, or goodwill, arising from the use of or
            inability to use the Platform. Our total aggregate liability arising out of or relating to the Service
            shall not exceed the subscription fees paid by the School for the Service in the twelve (12) months
            preceding the event giving rise to the claim.
          </Typography>
        </Section>

        <Section n="15" title="Indemnification">
          <Typography component="p" variant="body2">
            The School agrees to indemnify and hold harmless AJ Tech Labs against claims, damages, and reasonable
            costs arising from the School's breach of these Terms, misuse of the Platform, or violation of
            applicable law in connection with its use of the Service.
          </Typography>
        </Section>

        <Section n="16" title="Changes to These Terms">
          <Typography component="p" variant="body2">
            We may update these Terms from time to time. Material changes will be notified through the Platform or
            by email. Continued use of the Service after changes take effect constitutes acceptance of the updated
            Terms.
          </Typography>
        </Section>

        <Section n="17" title="Governing Law & Jurisdiction">
          <Typography component="p" variant="body2">
            These Terms are governed by the laws of India. The courts at Hyderabad, Telangana shall have exclusive
            jurisdiction over any dispute arising from or relating to these Terms or the Service.
          </Typography>
        </Section>

        <Section n="18" title="Contact">
          <Typography component="p" variant="body2">
            For any questions about these Terms, contact AJ Tech Labs at{' '}
            <Link href="mailto:support@classtree.in" underline="hover">support@classtree.in</Link>,
            Kondapur, Hyderabad.
          </Typography>
        </Section>

        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary">
          Questions about these Terms? Contact us at{' '}
          <Link href="mailto:support@classtree.in" underline="hover">support@classtree.in</Link>. You can return to the{' '}
          <Link component={RouterLink} to="/login" underline="hover">login page</Link> at any time.
        </Typography>
      </Paper>
    </Container>
  );
};

export default Terms;
